import React, { useMemo } from 'react';
import { Loading } from '@shape-ds/components';
import { useRiskScoreQuery } from 'queries/risk/queries';
import { useTranslation } from 'react-i18next';
import {
  InfoMessage,
  LineChart,
  useChartVariables,
  ChartFilters,
  ChartLegend,
} from '@shape/lighthouse-components';
import { colorsPalette } from 'utils';
import '../style.scss';
import { mountUnitAxis } from '../helpers/elementsParse';
import { useInterpolationData } from '../hooks/useInterpolationData';

export type SensorsChartsProps = {
  modelName?: string;
  hasAlarmPeriod?: boolean;
  overrideGraphicOptions?: LineChartsProps['overrideGraphicOptions'];
  alarmsInfo?: LineChartsProps['alarmsInfo'];
  children?: React.ReactNode;
  showTitle?: boolean;
};

const RiskScoreChart: React.FC<SensorsChartsProps> = ({
  hasAlarmPeriod,
  overrideGraphicOptions,
  children,
  modelName,
  alarmsInfo,
  showTitle = true,
}) => {
  const { dateRange } = useChartVariables();
  const { t } = useTranslation();
  const window = useInterpolationData(dateRange);

  const { data, isFetching, isError } = useRiskScoreQuery(
    {
      modelName,
      startDate: dateRange.start,
      endDate: dateRange.end,

      window,
    },
    {
      enabled: !!modelName && !!window,
    },
  );

  const tagsConfig = useMemo(() => {
    const map = new Map();
    map.set(t('components:risk_score_chart.risk'), {
      label: t('components:risk_score_chart.risk'),
      category: [],
      color: colorsPalette[0],
      unit_of_measurement: t('components:risk_score_chart.unit'),
      reference: t('components:risk_score_chart.risk'),
    });
    return map;
  }, []);

  if (isFetching) {
    return (
      <>
        {showTitle && (
          <h2 className="risk-score-chart-box-title">{t('model_page:sections.risk')}</h2>
        )}
        <ChartFilters enableCustomRange hasAlarmPeriod={hasAlarmPeriod} />
        <div className="line-chart">
          <Loading />
        </div>
      </>
    );
  }

  if (
    !data?.predictions ||
    data?.predictions.length === 0 ||
    data?.predictions === null ||
    data?.predictions === undefined
  ) {
    return (
      <>
        {showTitle && (
          <h2 className="risk-score-chart-box-title">{t('model_page:sections.risk')}</h2>
        )}
        <ChartFilters
          enableCustomRange
          hasAlarmPeriod={hasAlarmPeriod}
          data-testid="chart-filters"
        />
        <div className="scaling-error-container">
          <InfoMessage
            type="warning"
            title={t('messages:no_data.title')}
            message={t('messages:no_data.message')}
            data-testid="no-data-message"
          />
        </div>
      </>
    );
  }

  return (
    <>
      {showTitle && <h2 className="risk-score-chart-box-title">{t('model_page:sections.risk')}</h2>}
      <ChartFilters enableCustomRange hasAlarmPeriod={hasAlarmPeriod} data-testid="chart-filters" />

      {children}

      <LineChart
        chartData={data?.predictions ?? []}
        tagsConfig={tagsConfig}
        isFetching={isFetching}
        isError={isError}
        alarmsInfo={alarmsInfo}
        data-testid="line-chart"
      />

      <ChartLegend
        showDescription={false}
        thereshold={{
          isDashed: true,
          label: t('components:risk_score_chart.high_risk'),
          color: '#E43030',
          enabled: true,
          reference: t('components:risk_score_chart.high_risk'),
        }}
      />
    </>
  );
};

export default RiskScoreChart;
